import React, { useState, useMemo, useRef } from 'react'
import {
  Box,
  Button,
  Flex,
  Spinner,
  Center,
  useToast,
  Icon,
  HStack,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  FaArrowLeft,
  FaSync,
  FaUpload,
  FaStar,
  FaRegStar,
} from 'react-icons/fa'

import DataEntryTable from '../components/DataEntryTable'
import AddEntry from '../components/AddEntry'
import { dataService, subscriptionService } from '../services/api'
import useAuth from '../hooks/useAuth'
import type { DataEntriesWithoutEntriesResponse, EntryRecord } from '../types'

const DataDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const recordId = Number(id)
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const { userId } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  const [isUploading, setIsUploading] = useState(false)
  const [isSubscribing, setIsSubscribing] = useState(false)
  const [isUnsubscribing, setIsUnsubscribing] = useState(false)

  // Query for fetching data entries
  const { data, isLoading, error, refetch } = useQuery<{
    record: EntryRecord
    files: DataEntriesWithoutEntriesResponse['data']['files']
    user: DataEntriesWithoutEntriesResponse['data']['user']
    features_original?: DataEntriesWithoutEntriesResponse['data']['features_original']
    features_sorted?: DataEntriesWithoutEntriesResponse['data']['features_sorted']
  }>({
    queryKey: ['dataEntries', recordId],
    queryFn: async () => {
      if (!recordId || isNaN(recordId)) {
        throw new Error('Invalid record ID')
      }

      const base = await dataService.getDataEntriesWithoutEntries(recordId)

      const baseRecord = base.data.record
      if (!baseRecord) {
        throw new Error('Record not found')
      }

      return {
        record: {
          ...baseRecord,
          symbols: base.data.symbols ?? baseRecord.symbols ?? [],
          target_assets: base.data.target_assets ?? baseRecord.target_assets ?? [],
        },
        files: base.data.files ?? [],
        user: base.data.user,
        features_original: base.data.features_original,
        features_sorted: base.data.features_sorted,
      }
    },
    enabled: !!recordId && !isNaN(recordId),
  })

  // Query for getting all user subscriptions
  const { data: subscriptionData, refetch: refetchSubscription } = useQuery({
    queryKey: ['subscriptions'],
    queryFn: async () => {
      return await subscriptionService.getAllSubscriptions()
    },
    enabled: !!userId,
  })

  const isOwner = useMemo(() => {
    return !!userId && !!data?.record?.user_id && userId === data.record.user_id
  }, [userId, data?.record?.user_id])

  const isSubscribed = useMemo(() => {
    if (!userId || !recordId || subscriptionData === undefined) return null
    if (!subscriptionData.data?.subscriptions) return null
    return subscriptionData.data.subscriptions.some(
      (sub) => sub.record_id === recordId && sub.subscription.is_active
    )
  }, [userId, recordId, subscriptionData])

  const handleRefresh = () => {
    refetch()
    queryClient.invalidateQueries({ queryKey: ['entries', recordId] })
    toast({
      title: 'Refreshing data',
      status: 'info',
      duration: 2000,
      isClosable: true,
    })
  }

  const handleBack = () => {
    // Check if we can go back in history
    if (location.key !== 'default') {
      navigate(-1) // Navigate to previous page in history
    } else {
      // If there's no history (e.g., opened in new tab), go to the records page
      navigate('/data')
    }
  }

  // Open file browser when user clicks upload button
  const handleUploadFile = () => {
    // Trigger the hidden file input click to open the file system dialog
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  // Handle the selected file after user chooses it
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files
    if (!files || files.length === 0 || !recordId) return

    const file = files[0]
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || ''

    // Validate file extension (CSV or TXT only)
    if (!['csv', 'txt'].includes(fileExtension)) {
      toast({
        title: 'Invalid file format',
        description: 'Please upload only CSV or TXT files',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    setIsUploading(true)

    try {
      // Create form data
      const formData = new FormData()
      formData.append('record_id', recordId.toString())
      formData.append('file', file)

      // Call the upload API
      const response = await dataService.uploadFile(formData)

      if (response.status === 'success') {
        toast({
          title: 'File uploaded successfully',
          description: `${file.name} has been uploaded`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        })

        // Update the files list by refreshing the data
        refetch()
      } else {
        throw new Error(response.message || 'Failed to upload file')
      }
    } catch (error) {
      toast({
        title: 'Upload failed',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsUploading(false)
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleSubscribe = async () => {
    if (!recordId) return

    setIsSubscribing(true)
    try {
      const response = await subscriptionService.subscribe(recordId)

      toast({
        title: 'Subscribed successfully',
        description: `You now have access until ${new Date(response.data.expires_at).toLocaleDateString()}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      })

      refetchSubscription()
    } catch (error) {
      toast({
        title: 'Subscription failed',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsSubscribing(false)
    }
  }

  const handleUnsubscribe = async () => {
    if (!recordId) return

    setIsUnsubscribing(true)
    try {
      const response = await subscriptionService.cancelSubscription(recordId)

      toast({
        title: 'Unsubscribed successfully',
        description: response.message,
        status: 'info',
        duration: 5000,
        isClosable: true,
      })

      refetchSubscription()
    } catch (error) {
      toast({
        title: 'Failed to unsubscribe',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsUnsubscribing(false)
    }
  }

  return (
    <Box p={4}>
      <Flex
        mb={8}
        align="center"
        justifyContent="space-between"
        bg={useColorModeValue('white', 'gray.800')}
        p={4}
        borderRadius="lg"
        boxShadow="sm"
        borderWidth="1px"
        borderColor={useColorModeValue('gray.200', 'gray.700')}
      >
        <Button
          leftIcon={<Icon as={FaArrowLeft} />}
          onClick={handleBack}
          variant="ghost"
          size="md"
          _hover={{ bg: useColorModeValue('gray.100', 'gray.700') }}
          fontWeight="medium"
          px={4}
        >
          Back to Records
        </Button>

        <HStack spacing={4} alignItems="center">
          {isOwner && (
            <>
              <Tooltip
                hasArrow
                label="Upload CSV or TXT files only"
                placement="top"
                bg="blue.600"
                color="white"
                fontSize="sm"
                px={3}
                py={2}
              >
                <Button
                  leftIcon={<Icon as={FaUpload} />}
                  colorScheme="blue"
                  onClick={handleUploadFile}
                  isLoading={isUploading}
                  loadingText="Uploading"
                  size="md"
                  fontWeight="semibold"
                  boxShadow="sm"
                  _hover={{
                    transform: 'translateY(-2px)',
                    boxShadow: 'md',
                  }}
                  transition="all 0.2s"
                >
                  Upload File
                </Button>
              </Tooltip>
              <AddEntry recordId={recordId} onSuccess={refetch} />
            </>
          )}
          {!isOwner &&
            isSubscribed !== null &&
            (isSubscribed ? (
              <Button
                leftIcon={<Icon as={FaStar} />}
                colorScheme="yellow"
                onClick={handleUnsubscribe}
                isLoading={isUnsubscribing}
                loadingText="Cancelling"
                size="md"
                fontWeight="semibold"
                boxShadow="sm"
              >
                Cancel Subscription
              </Button>
            ) : (
              <Button
                leftIcon={<Icon as={FaRegStar} />}
                colorScheme="purple"
                onClick={handleSubscribe}
                isLoading={isSubscribing}
                loadingText="Subscribing"
                size="md"
                fontWeight="semibold"
                boxShadow="sm"
              >
                Subscribe
              </Button>
            ))}

          <Button
            leftIcon={<Icon as={FaSync} />}
            onClick={handleRefresh}
            isLoading={isLoading}
            variant="outline"
            colorScheme="green"
            size="md"
            fontWeight="medium"
            _hover={{
              bg: useColorModeValue('green.50', 'green.900'),
            }}
          >
            Refresh
          </Button>
        </HStack>
      </Flex>

      {/* Hidden file input - only accepts CSV and TXT files */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
        accept=".csv,.txt"
      />

      {isLoading && !data ? (
        <Center h="60vh">
          <Spinner size="xl" color="blue.500" />
        </Center>
      ) : (
        <DataEntryTable
          recordId={recordId}
          data={data ?? null}
          isLoading={isLoading}
          error={error}
        />
      )}
    </Box>
  )
}

export default DataDetailPage
